import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Check } from "lucide-react";
import { RentalType } from "@/shared/types/enums";
import type { TagResponseDTO } from "@/features/tags/types";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";
import type { RentalResponseDTO } from "@/shared/types/rental.types";
import type { CreateRentalDTO, PaymentDTO } from "../../types";
import type {
  Step1Data,
  Step2Data,
  Step3Data,
  NewClientData,
} from "../../schemas";
import type { ContractOption, ClientOption } from "./ContractSelector";
import { dateLocalToIso, isoToDateLocal } from "@/shared/lib/date-format";
import { WizardStep1 } from "./WizardStep1";
import { WizardStep2 } from "./WizardStep2";
import { WizardStep3 } from "./WizardStep3";

interface RentalWizardProps {
  open: boolean;
  /** Aluguel em edição — este dialog não cria, só edita. */
  editing: RentalResponseDTO;
  sellers: SellerResponseDTO[];
  tags: TagResponseDTO[];
  gownProducts: ProductResponseDTO[];
  searchContracts: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ContractOption[]>;
  createContract: (
    contractNumber: string,
    clientId: number,
    signal?: AbortSignal,
  ) => Promise<{ id: number; contractNumber: string }>;
  searchClients: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ClientOption[]>;
  createClient: (
    data: NewClientData,
    signal?: AbortSignal,
  ) => Promise<{ id: number; name: string }>;
  searchStocks: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<StockResponseDTO[]>;
  onSubmit: (data: CreateRentalDTO) => Promise<void>;
  onClose: () => void;
}

const STEP_LABELS = ["Contrato e vendedor", "Itens", "Valores e datas"];

export const RentalWizard = ({
  open,
  editing,
  sellers,
  tags,
  gownProducts,
  searchContracts,
  createContract,
  searchClients,
  createClient,
  searchStocks,
  onSubmit,
  onClose,
}: RentalWizardProps) => {
  const [step, setStep] = useState(1);
  const [maxReachedStep, setMaxReachedStep] = useState(1);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [step2Data, setStep2Data] = useState<Step2Data | null>(null);
  // Snapshot contínuo do passo 3 — sem ele, navegar para os passos 1/2
  // desmonta o form e perde pagamentos/valores digitados.
  const [step3Data, setStep3Data] = useState<Partial<Step3Data> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setStep3Data(null);
    setStep1Data({
      contractId: editing.contractId,
      contractDisplay: editing.contractNumber,
      sellerId: editing.sellerId,
    });
    setStep2Data({
      rentalType: editing.rentalType,
      stockIds: editing.stockItems?.map((s) => s.id) ?? editing.stockIds ?? [],
      gownItems: editing.gownItems.map((g) => ({
        productId: g.productId,
        quantity: g.quantity,
      })),
      hasShoes: editing.hasShoes,
      shoesPairQuantity: editing.shoesPairQuantity,
      description: editing.description ?? "",
      tagIds: editing.tagIds,
    });
    setMaxReachedStep(3);
  }, [open, editing]);

  const handleClose = () => {
    setStep(1);
    setStep1Data(null);
    setStep2Data(null);
    setStep3Data(null);
    setMaxReachedStep(1);
    onClose();
  };

  const handleStep1Next = (data: Step1Data) => {
    setStep1Data(data);
    setStep(2);
    setMaxReachedStep((prev) => Math.max(prev, 2));
  };

  const handleStep2Next = (data: Step2Data) => {
    setStep2Data(data);
    setStep(3);
    setMaxReachedStep((prev) => Math.max(prev, 3));
  };

  const handleStep3Submit = async (data: Step3Data) => {
    if (!step1Data || !step2Data) return;
    setIsSubmitting(true);
    try {
      const isSuit = step2Data.rentalType === RentalType.SUIT;
      const dto: CreateRentalDTO = {
        contractId: step1Data.contractId,
        sellerId: step1Data.sellerId,
        rentalType: step2Data.rentalType,
        // Preserva o rentedAt original — reenviar "agora" corrompia filtros
        // por período, ordenação e competência de comissão.
        rentedAt: editing.rentedAt,
        hasShoes: isSuit ? step2Data.hasShoes : false,
        ...(isSuit && step2Data.hasShoes
          ? { shoesPairQuantity: step2Data.shoesPairQuantity }
          : {}),
        stockIds: isSuit ? step2Data.stockIds : undefined,
        gownItems: !isSuit ? step2Data.gownItems : undefined,
        description: step2Data.description,
        tagIds: step2Data.tagIds,
        totalValue: data.totalValue,
        // Interpreta como meia-noite local (não UTC) antes de converter
        // para ISO 8601. Preserva a data civil escolhida pelo usuário —
        // ver dateLocalToIso.
        expectedOutAt: dateLocalToIso(data.expectedOutAt),
        expectedInAt: dateLocalToIso(data.expectedInAt),
        // Aluguel retroativo: envia as datas efetivas em que a peça
        // saiu e voltou. Só quando o admin marca o checkbox.
        ...(data.isRetroactive && data.outAt && data.inAt
          ? {
              outAt: dateLocalToIso(data.outAt),
              inAt: dateLocalToIso(data.inAt),
            }
          : {}),
        // O form começa vazio e os itens adicionados são NOVOS pagamentos —
        // o backend soma ao paidValue e gera as NFs; os existentes são
        // imutáveis. `emitInvoice` só vale na criação, então não é enviado.
        payments: data.payments as PaymentDTO[],
        // A observação só acompanha pagamentos novos (é a observação das NFs
        // deles); sem pagamento novo seria órfã.
        ...(data.invoiceObservation?.trim() && data.payments.length > 0
          ? { invoiceObservation: data.invoiceObservation.trim() }
          : {}),
      };
      await onSubmit(dto);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const step1Defaults = {
    contractId: editing.contractId,
    contractDisplay: editing.contractNumber,
    sellerId: editing.sellerId,
  };

  const step2Defaults = {
    rentalType: editing.rentalType,
    stockIds: editing.stockItems?.map((s) => s.id) ?? editing.stockIds ?? [],
    gownItems: editing.gownItems.map((g) => ({
      productId: g.productId,
      quantity: g.quantity,
    })),
    hasShoes: editing.hasShoes,
    shoesPairQuantity: editing.shoesPairQuantity,
    description: editing.description,
    tagIds: editing.tagIds,
  };

  // O snapshot do passo 3 tem prioridade: preserva o que foi digitado ao
  // navegar entre passos (o form do passo 3 é desmontado fora dele).
  const step3Defaults = step3Data ?? {
    totalValue: editing.totalValue,
    // Extrai a data no fuso local — evita off-by-one quando a data
    // original foi armazenada em horário próximo do fim do dia UTC.
    expectedOutAt: isoToDateLocal(editing.expectedOutAt),
    expectedInAt: isoToDateLocal(editing.expectedInAt),
    // Se o aluguel foi salvo como retroativo (tem outAt/inAt
    // populados), pré-marca o checkbox e as datas efetivas.
    isRetroactive: !!(editing.outAt && editing.inAt),
    outAt: editing.outAt ? isoToDateLocal(editing.outAt) : "",
    inAt: editing.inAt ? isoToDateLocal(editing.inAt) : "",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar aluguel</DialogTitle>
        </DialogHeader>

        <nav className="flex items-center mb-2">
          {STEP_LABELS.map((label, i) => {
            const stepNum = i + 1;
            const isCompleted = stepNum < step;
            const isCurrent = stepNum === step;
            const isAccessible = stepNum <= maxReachedStep;

            return (
              <div key={i} className="flex items-center flex-1 last:flex-none">
                <button
                  type="button"
                  disabled={!isAccessible}
                  onClick={() => isAccessible && setStep(stepNum)}
                  className="flex flex-col items-center gap-1.5 group disabled:cursor-not-allowed"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors
                    ${isCurrent ? "border-primary bg-primary text-primary-foreground" : ""}
                    ${isCompleted ? "border-primary bg-primary/15 text-primary" : ""}
                    ${!isCurrent && !isCompleted ? "border-muted-foreground/30 bg-muted text-muted-foreground" : ""}
                  `}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
                  </div>
                  <span
                    className={`text-xs whitespace-nowrap transition-colors
                    ${isCurrent ? "text-foreground font-medium" : ""}
                    ${isCompleted ? "text-primary" : ""}
                    ${!isCurrent && !isCompleted ? "text-muted-foreground" : ""}
                  `}
                  >
                    {label}
                  </span>
                </button>
                {i < STEP_LABELS.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-3 mb-5 transition-colors ${stepNum < step ? "bg-primary" : "bg-muted"}`}
                  />
                )}
              </div>
            );
          })}
        </nav>

        {step === 1 && (
          <WizardStep1
            defaultValues={step1Defaults}
            sellers={sellers}
            searchContracts={searchContracts}
            createContract={createContract}
            searchClients={searchClients}
            createClient={createClient}
            onNext={handleStep1Next}
          />
        )}
        {step === 2 && (
          <WizardStep2
            defaultValues={step2Defaults}
            tags={tags}
            gownProducts={gownProducts}
            searchStocks={searchStocks}
            onNext={handleStep2Next}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <WizardStep3
            defaultValues={step3Defaults}
            isEditing
            currentPaidValue={editing.paidValue}
            rentalEmitInvoice={editing.emitInvoice}
            isSubmitting={isSubmitting}
            onValuesChange={setStep3Data}
            onSubmit={handleStep3Submit}
            onBack={() => setStep(2)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
